import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { itemApi } from '../api/itemApi'

export function useGetItems(
  params?: { status?: string; productId?: string; departmentId?: string; excludeDepartmentId?: string },
  enabled = true,
) {
  return useQuery({
    queryKey: ['items', params],
    queryFn: () => itemApi.getItems(params),
    select: response => response.items,
    enabled,
  })
}

export function useGetItem(id?: string, enabled = true) {
  return useQuery({
    queryKey: ['item', id],
    queryFn: () => itemApi.getItem(id!),
    enabled: enabled && !!id,
  })
}

export function useGetItemTransfer(id?: string, enabled = true) {
  return useQuery({
    queryKey: ['item-transfer', id],
    queryFn: () => itemApi.getItemTransfer(id!),
    enabled: enabled && !!id,
  })
}

export function useGetItemLogs(id?: string, enabled = false) {
  return useQuery({
    queryKey: ['itemLogs', id],
    queryFn: () => itemApi.getItemLogs(id!),
    enabled: enabled && !!id,
  })
}

export function useCreateItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ productId, payload }: { productId: string; payload: Partial<any> }) =>
      itemApi.createItem(productId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-items'] })
      queryClient.invalidateQueries({ queryKey: ['items'] })
    },
  })
}

export function useUpdateItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<any> }) => itemApi.updateItem(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-items'] })
      queryClient.invalidateQueries({ queryKey: ['Item'] })
    },
  })
}

export function useUpdateItemStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { status: string; note?: string } }) =>
      itemApi.updateItemStatus(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['item', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['items'] })
      queryClient.invalidateQueries({ queryKey: ['product-items'] })
    },
  })
}

export function useDeleteItem() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => itemApi.deleteItem(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['product-items'] })
      queryClient.invalidateQueries({ queryKey: ['items'] })
    },
  })
}

export function useGetItemUsage(id?: string, enabled = true) {
  return useQuery({
    queryKey: ['item-usages', id],
    queryFn: () => itemApi.getItemUsage(id!),
    enabled: enabled && !!id,
  })
}

export function useCreateItemUsage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { patientId: string; note: string } }) =>
      itemApi.createItemUsage(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['item-usages', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['item', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['items'] })
      queryClient.invalidateQueries({ queryKey: ['patient-items'] })
      queryClient.invalidateQueries({ queryKey: ['department-items'] })
      queryClient.invalidateQueries({ queryKey: ['beds'] })
    },
  })
}

export function useEndItemUsage() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { note?: string } }) => itemApi.endItemUsage(id, payload),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['item-usages', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['item', variables.id] })
      queryClient.invalidateQueries({ queryKey: ['items'] })
      queryClient.invalidateQueries({ queryKey: ['patient-items'] })
      queryClient.invalidateQueries({ queryKey: ['department-items'] })
      queryClient.invalidateQueries({ queryKey: ['beds'] })
    },
  })
}
